import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, User, Menu, X, Heart, Search, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import SearchOverlay from "@/components/SearchOverlay";
import TrustpilotStrip from "@/components/TrustpilotStrip";
import VaultProgressBadge from "@/components/VaultProgressBadge";
import { useAuth } from "@/context/AuthContext";
import { useSection } from "@/context/SiteContentContext";

// Two-track navigation — B2C (Shop / Accessories / Collections / Club / Vote)
// and B2B/Bespoke (Custom / Start a Brand / Bespoke / Trade). Split signals
// the funnel split per strategy doc — keep retail and trade journeys distinct.
// Each PRIMARY_NAV item declares a Tailwind `show` class that controls its
// breakpoint visibility — items not shown at the current breakpoint fall into
// the "More" dropdown so the header never overlaps the logo on narrow desktops.
const PRIMARY_NAV = [
  { to: "/shop", label: "Shop", show: "flex" },
  { to: "/custom", label: "Custom", show: "flex" },
  { to: "/club", label: "Moto Club", show: "flex" },
  { to: "/start-your-brand", label: "Start a Brand", show: "flex" },
  { to: "/trade", label: "Trade", show: "flex" },
  { to: "/collections", label: "Collections", show: "hidden xl:flex" },
];

const MORE_NAV = [
  { to: "/the-vault", label: "The Vault", show: "flex" },
  { to: "/journal", label: "Journal", show: "flex" },
  { to: "/reviews", label: "Reviews", show: "flex" },
  { to: "/collections", label: "Collections", show: "flex xl:hidden" },
  { to: "/accessories", label: "Accessories", show: "flex" },
  { to: "/vote", label: "Vote", show: "flex" },
  { to: "/bespoke", label: "Bespoke", show: "flex" },
  { to: "/gift-cards", label: "Gift Cards", show: "flex" },
  { to: "/share-your-story", label: "Your Story", show: "flex" },
  { to: "/about", label: "About", show: "flex" },
  { to: "/contact", label: "Contact", show: "flex" },
];

// Mobile drawer keeps the full flat list (no nested menu).
const NAV = [...PRIMARY_NAV, ...MORE_NAV];

const HEADER_FALLBACK = {
  logo_url: "https://customer-assets.emergentagent.com/job_collection-catalog/artifacts/n8hgnnc8_mws.jpg",
  announcement: "Free UK shipping over £65 · Limited drops · Studio in Norwich, UK",
};

export default function Header() {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const content = useSection("header", HEADER_FALLBACK);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);

  // Cmd/Ctrl+K opens the search overlay anywhere on the site.
  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Lock body scroll while the mobile drawer is open
  React.useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [mobileOpen]);

  const closeDrawer = () => setMobileOpen(false);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const moreRef = React.useRef(null);

  // Close the More dropdown when clicking outside or pressing Escape.
  React.useEffect(() => {
    if (!moreOpen) return undefined;
    const onDown = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMoreOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-black/10" data-testid="site-header">
      {/* Auth strip — sits at the very top of the header so it's the first thing every visitor sees, on every viewport. */}
      <div className="bg-white border-b border-black/10 px-4 sm:px-6 lg:px-12 py-1.5 flex items-center justify-end gap-3 sm:gap-4 text-[10px] tracking-[0.22em] uppercase font-semibold" data-testid="mobile-auth-strip">
        {user ? (
          <>
            {(typeof user.loyalty_points === "number") && (
              <span className="hidden sm:inline text-[10px] text-black/55 normal-case tracking-normal font-normal" data-testid="auth-loyalty-greeting">
                Hi {user.name?.split(" ")[0] || "there"} · {user.loyalty_points.toLocaleString("en-GB")} pts
              </span>
            )}
            <Link to="/account" className="px-3 py-1.5 border border-black hover:bg-black hover:text-white transition-colors flex items-center gap-1.5 min-h-[36px]" data-testid="mobile-auth-account">
              <User size={12} strokeWidth={1.75} aria-hidden="true" />
              {user.name?.split(" ")[0] || "Account"}
            </Link>
            {user.role === "admin" && (
              <Link to="/studio-hq" className="px-3 py-1.5 bg-[#FF3B30] text-white min-h-[36px] flex items-center" data-testid="mobile-auth-admin">Admin</Link>
            )}
          </>
        ) : (
          <>
            <Link to="/login" className="px-3 py-1.5 border border-black hover:bg-black hover:text-white transition-colors min-h-[36px] flex items-center" data-testid="mobile-auth-login">Login</Link>
            <Link to="/register" className="px-3 py-1.5 bg-black text-white hover:bg-[#FF3B30] transition-colors min-h-[36px] flex items-center" data-testid="mobile-auth-register">Create account</Link>
          </>
        )}
      </div>

      <div className="bg-moto-gradient text-white text-center py-2 text-[10px] md:text-[11px] tracking-[0.22em] uppercase font-semibold" data-testid="announcement-bar">
        {content.announcement}
      </div>
      <div
        className="bg-black text-white/90 text-center py-2 px-4 text-[9px] md:text-[10px] tracking-[0.22em] uppercase font-semibold"
        data-testid="arcade-teaser-bar"
        title="A secret arcade hides somewhere on this site"
      >
        {/* Short single-line teaser on mobile, full multi-line clue rail on tablet+ */}
        <div className="md:hidden">Find the hidden game · 25% off awaits</div>
        <div className="hidden md:block">Find the hidden link to the game — guaranteed discovery prize · in-game laser/shield drops · 3-month leaderboard race · 2 free tees at every millionth game</div>
        <div className="mt-0.5 text-white/60 text-[8px] md:text-[9px] hidden md:block">
          <span className="text-[#FF3B30] font-bold">Clue:</span> the link looks out of place on the site
        </div>
        <div className="mt-0.5 text-white/40 text-[8px] md:text-[9px] hidden md:block" data-testid="codebreaker-hint">
          <span className="text-[#FF3B30] font-bold">Bonus:</span> a six-letter code lives in our six random pages — find the way to type the magic word to claim 25% off
        </div>
      </div>
      <TrustpilotStrip />

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8 lg:px-8 xl:px-12 h-20 md:h-24 lg:h-24 xl:h-28 flex items-center gap-3 lg:gap-4 xl:gap-8 justify-between">
        <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
          <img
            src={content.logo_url}
            alt="MotoWear"
            className="h-12 md:h-16 lg:h-14 xl:h-20 2xl:h-24 w-auto object-contain select-none drop-shadow-[0_2px_10px_rgba(0,0,0,0.22)]"
            draggable="false"
          />
        </Link>
        <nav className="hidden lg:flex items-center gap-4 xl:gap-6 2xl:gap-8 flex-1 justify-center min-w-0" data-testid="nav-desktop">
          {PRIMARY_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `${n.show} label-eyebrow text-[0.65rem] xl:text-[0.7rem] tracking-[0.18em] xl:tracking-[0.22em] text-black/70 hover:text-black transition-colors whitespace-nowrap ${isActive ? "text-black border-b border-black pb-0.5" : ""}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <div className="relative shrink-0" ref={moreRef}>
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
              data-testid="nav-more-trigger"
              className={`label-eyebrow text-[0.65rem] xl:text-[0.7rem] tracking-[0.18em] xl:tracking-[0.22em] whitespace-nowrap flex items-center gap-1 px-3 py-1.5 border transition-colors ${moreOpen ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "bg-black text-white border-black hover:bg-[#FF3B30] hover:border-[#FF3B30]"}`}
            >
              More
              <ChevronDown size={14} strokeWidth={1.75} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {moreOpen && (
              <div
                role="menu"
                data-testid="nav-more-menu"
                className="absolute right-0 mt-3 min-w-[220px] bg-white border border-black/10 shadow-2xl py-2 z-50"
              >
                {MORE_NAV.map((n) => (
                  <NavLink
                    key={`${n.to}-${n.label}`}
                    to={n.to}
                    onClick={() => setMoreOpen(false)}
                    role="menuitem"
                    data-testid={`nav-more-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `${n.show || "flex"} px-4 py-2.5 label-eyebrow text-xs whitespace-nowrap ${isActive ? "text-[#FF3B30]" : "text-black/75 hover:bg-black hover:text-white"} transition-colors`
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3 lg:gap-3 xl:gap-5 shrink-0">
          <VaultProgressBadge />
          {user?.role === "admin" && (
            <Link to="/studio-hq" className="hidden xl:block label-eyebrow text-[#FF3B30]" data-testid="nav-admin">Admin</Link>
          )}
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center justify-center label-eyebrow hover:moto-accent transition-colors min-w-[40px] min-h-[40px]"
            aria-label="Search the site"
            data-testid="header-search-button"
          >
            <Search size={18} strokeWidth={1.5} aria-hidden="true" />
          </button>
          {user ? (
            <>
              <Link to="/wishlist" className="hidden lg:flex items-center label-eyebrow text-black/70 hover:text-black min-w-[40px] min-h-[40px] justify-center" data-testid="nav-wishlist" title="Wishlist" aria-label="Wishlist">
                <Heart size={16} strokeWidth={1.5} aria-hidden="true" />
              </Link>
              <Link to="/account" className="hidden lg:flex items-center gap-1.5 label-eyebrow text-[0.65rem] xl:text-[0.7rem] tracking-[0.18em] xl:tracking-[0.22em]" data-testid="nav-account" title="Account">
                <User size={16} strokeWidth={1.5} aria-hidden="true" />
                <span className="hidden xl:inline">{user.name?.split(" ")[0] || "Account"}</span>
              </Link>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden lg:block label-eyebrow text-[0.65rem] xl:text-[0.7rem] tracking-[0.18em] xl:tracking-[0.22em] px-2 py-2 hover:moto-accent transition-colors" data-testid="nav-login">Login</Link>
              <Link to="/register" className="hidden xl:block label-eyebrow px-3 py-2 bg-black text-white hover:bg-[#FF3B30] transition-colors" data-testid="nav-register">Create account</Link>
            </>
          )}
          <button
            onClick={() => setOpen(true)}
            className="relative flex items-center justify-center gap-1.5 label-eyebrow min-w-[40px] min-h-[40px] hover:moto-accent transition-colors"
            aria-label={`Open cart${count ? ` — ${count} items` : ""}`}
            data-testid="header-cart-button"
          >
            <ShoppingBag size={20} strokeWidth={1.75} aria-hidden="true" />
            <span className="hidden sm:inline lg:hidden xl:inline">Cart</span>
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#FF3B30] text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full font-semibold shadow-[0_2px_6px_rgba(255,59,48,0.45)]" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>
          <button
            className="lg:hidden flex items-center justify-center min-w-[44px] min-h-[44px]"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — scrollable, contains the FULL nav (and account/admin shortcuts for logged-in users). Login/Register stay in the auth strip above. */}
      {mobileOpen && (
        <div
          id="mobile-nav"
          className="lg:hidden fixed inset-x-0 top-0 z-50 bg-white border-b border-black/15 shadow-2xl flex flex-col"
          style={{ maxHeight: "100dvh", paddingTop: "env(safe-area-inset-top, 0px)" }}
          data-testid="mobile-nav"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-black/10 shrink-0">
            <Link to="/" onClick={closeDrawer} className="flex items-center" data-testid="mobile-nav-logo">
              <img src={content.logo_url} alt="MotoWear" className="h-10 w-auto object-contain" draggable="false" />
            </Link>
            <button
              onClick={closeDrawer}
              aria-label="Close navigation"
              className="flex items-center justify-center min-w-[44px] min-h-[44px]"
              data-testid="mobile-nav-close"
            >
              <X size={22} aria-hidden="true" />
            </button>
          </div>

          <nav className="overflow-y-auto overscroll-contain flex-1 px-5 py-4" data-testid="mobile-nav-list">
            <ul className="space-y-1">
              {NAV.map((n) => (
                <li key={n.to}>
                  <NavLink
                    to={n.to}
                    onClick={closeDrawer}
                    className={({ isActive }) =>
                      `block py-3 px-2 label-eyebrow text-base border-b border-black/5 ${isActive ? "text-[#FF3B30]" : "text-black hover:text-[#FF3B30]"}`
                    }
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {n.label}
                  </NavLink>
                </li>
              ))}
              {user && (
                <>
                  <li>
                    <NavLink
                      to="/account"
                      onClick={closeDrawer}
                      className="block py-3 px-2 label-eyebrow text-base border-b border-black/5"
                      data-testid="mobile-nav-account"
                    >Account</NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/wishlist"
                      onClick={closeDrawer}
                      className="block py-3 px-2 label-eyebrow text-base border-b border-black/5"
                      data-testid="mobile-nav-wishlist"
                    >Wishlist</NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/orders"
                      onClick={closeDrawer}
                      className="block py-3 px-2 label-eyebrow text-base border-b border-black/5"
                      data-testid="mobile-nav-orders"
                    >Orders</NavLink>
                  </li>
                  {user.role === "admin" && (
                    <li>
                      <NavLink
                        to="/studio-hq"
                        onClick={closeDrawer}
                        className="block py-3 px-2 label-eyebrow text-base text-[#FF3B30] border-b border-black/5"
                        data-testid="mobile-nav-admin"
                      >Admin</NavLink>
                    </li>
                  )}
                </>
              )}
              <li>
                <button
                  type="button"
                  onClick={() => { closeDrawer(); setSearchOpen(true); }}
                  className="w-full text-left py-3 px-2 label-eyebrow text-base border-b border-black/5"
                  data-testid="mobile-nav-search"
                >Search</button>
              </li>
            </ul>
          </nav>

          <div className="border-t border-black/10 px-5 py-4 shrink-0" data-testid="mobile-nav-footer">
            {user ? (
              <p className="text-xs text-black/55">
                Signed in as <strong>{user.email}</strong>
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/login"
                  onClick={closeDrawer}
                  className="text-center py-3 border border-black label-eyebrow text-xs hover:bg-black hover:text-white transition-colors"
                  data-testid="mobile-nav-login"
                >Login</Link>
                <Link
                  to="/register"
                  onClick={closeDrawer}
                  className="text-center py-3 bg-black text-white label-eyebrow text-xs hover:bg-[#FF3B30] transition-colors"
                  data-testid="mobile-nav-register"
                >Create account</Link>
              </div>
            )}
          </div>
        </div>
      )}

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
